import type React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { hasLicense, LICENSE_KEY } from "../../../constants/licenses";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import { useEmailCompose } from "../../../hooks/use-email-compose";
import { useCreateFocusMatter } from "../../../hooks/use-focus-matters-api";
import type { MatchingResultItem } from "../../../hooks/use-free-matching-api";
import { useFreeMatchingApi } from "../../../hooks/use-free-matching-api";
import { useMatchingConditions } from "../../../hooks/use-matching-conditions-api";
import { useProposalComposer } from "../../../hooks/use-proposal-composer";
import { useCreatePipeline } from "../../../hooks/use-sales-pipelines-api";
import {
  type TalentDetail,
  useCreateTalent,
} from "../../../hooks/use-talents-api";
import { useCrmPermission } from "../../../hooks/useCrmPermission";
import { useEmailDetailRestApi } from "../../../hooks/useEmailDetailRestApi";
import { RecruitmentType } from "../../../hooks/useEmailsRestApi";
import { getCurrentTenant } from "../../../utils/authority-utils";
import { toastError } from "../../../utils/toast-utils";
import { HR_TYPE_TO_EMPLOYMENT_TYPE } from "../../crm/utils";
import { EmailDetailPage, type RightPanelMode } from "./EmailDetailPage";

export const EmailDetailPageContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [freeWords, setFreeWords] = useState<string[]>([]);
  const [rightPanelMode, setRightPanelMode] =
    useState<RightPanelMode>("results");
  const { currentUser } = useContext(AuthContext);

  // REST API を使用してメール詳細を取得
  const { data, loading, error } = useEmailDetailRestApi({
    variables: { id: id! },
    skip: !id,
  });

  const navigate = useNavigate();

  // ライセンス + CRM権限チェック
  const tenantLicenses = getCurrentTenant(currentUser)?.licenses;
  const isMatchingEnabled = hasLicense(tenantLicenses, LICENSE_KEY.MATCHING);
  const { canEdit: canCrmEdit } = useCrmPermission();

  // CRM連携フック
  const { createTalent, loading: isRegisteringTalent } = useCreateTalent();
  const { createMatter, loading: isRegisteringFocusMatter } =
    useCreateFocusMatter();
  const { createPipeline, loading: isAddingToPipeline } = useCreatePipeline();

  // マッチング API hook
  const {
    executeMatching,
    fetchExistingResults,
    updateResultStatus,
    upsertFeedback,
    clearResult,
    loading: matchingLoading,
    error: matchingError,
    jobResult,
  } = useFreeMatchingApi();

  // マッチング設定の状態を取得
  const {
    settings: matchingConditionsList,
    loading: matchingConditionsLoading,
  } = useMatchingConditions();
  const hasActiveMatchingCondition =
    !matchingConditionsLoading && matchingConditionsList.some(s => s.isActive);

  // 提案メール hook
  const proposal = useProposalComposer();

  // 返信メール hook
  const reply = useEmailCompose();

  // メール変更時にマッチング結果をリセット
  // biome-ignore lint/correctness/useExhaustiveDependencies: id is intentionally used to trigger reset on email change
  useEffect(() => {
    clearResult();
    proposal.resetDraft();
    reply.resetDraft();
    setRightPanelMode("results");
  }, [id, clearResult, proposal.resetDraft, reply.resetDraft]);

  // メールデータ取得後に既存マッチング結果を読み込む
  useEffect(() => {
    if (data?.email?.id && isMatchingEnabled) {
      fetchExistingResults(data.email.id);
    }
  }, [data?.email?.id, isMatchingEnabled, fetchExistingResults]);

  // URLのクエリパラメータからfreeWordsを取得
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const freeWordsParam = urlParams.get("freeWords");
    if (freeWordsParam) {
      setFreeWords(freeWordsParam.split(",").filter(word => word.trim()));
    }
  }, []);

  const handleExecuteMatching = useCallback(async () => {
    const email = data?.email;
    if (!email) return;

    const sourceType =
      email.recruitmentType === RecruitmentType.HumanResource ? "hr" : "matter";

    try {
      const result = await executeMatching(email.id, sourceType);
      if (result.resultsSaved > 0) {
        toast.success(
          `${result.candidatesSearched}件から${result.resultsSaved}件のマッチを発見しました`,
        );
      } else {
        toast.info("マッチする対向メールが見つかりませんでした");
      }
    } catch (e) {
      if (
        e instanceof Error &&
        "status" in e &&
        (e as { status: number }).status === 429
      ) {
        toastError("マッチングの利用上限に達しました。");
      } else {
        toastError("マッチングの実行に失敗しました。");
      }
    }
  }, [data?.email, executeMatching]);

  const handlePropose = useCallback(
    (result: MatchingResultItem) => {
      const email = data?.email;
      if (!email) return;
      proposal.startProposal(result, email);
      setRightPanelMode("compose");
    },
    [data?.email, proposal.startProposal],
  );

  const handleReply = useCallback(() => {
    const email = data?.email;
    if (!email) return;
    reply.startReply(email);
    setRightPanelMode("reply");
  }, [data?.email, reply.startReply]);

  const handleBackToResults = useCallback(() => {
    setRightPanelMode("results");
    reply.resetDraft();
  }, [reply.resetDraft]);

  // 詳細比較
  const [comparisonTarget, setComparisonTarget] =
    useState<MatchingResultItem | null>(null);

  const handleCompare = useCallback((result: MatchingResultItem) => {
    setComparisonTarget(result);
  }, []);

  const handleCloseComparison = useCallback(() => {
    setComparisonTarget(null);
  }, []);

  // CRM: メール→タレント登録
  const handleRegisterAsTalent = useCallback(async () => {
    const email = data?.email;
    if (!email?.hrColumn) return;
    const hr = email.hrColumn;

    try {
      const talent = await createTalent({
        name: email.from?.name || "（名前未設定）",
        email: email.from?.address || undefined,
        employmentType: hr.hrTypes
          ? HR_TYPE_TO_EMPLOYMENT_TYPE[hr.hrTypes] || "full_time"
          : "full_time",
        nearestStation: hr.station || undefined,
        desiredReward: hr.rewardInt || undefined,
        remotePreference: hr.remoteFrequency || undefined,
        age: hr.ageInt || undefined,
        sourceType: "email",
        sourceEmailId: email.id,
        sourceSnapshot: JSON.stringify(hr),
      } as Partial<TalentDetail>);
      toast.success("タレントとして登録しました");
      navigate(`/crm/talents/${talent.id}`);
    } catch {
      toast.error("タレント登録に失敗しました");
    }
  }, [data?.email, createTalent, navigate]);

  // CRM: メール→注力案件登録
  const handleRegisterAsFocusMatter = useCallback(async () => {
    const email = data?.email;
    if (!email?.matterColumn) return;
    const mc = email.matterColumn;

    try {
      await createMatter({
        title: email.subject || "（タイトル未設定）",
        clientCompany: email.from?.name || undefined,
        rewardMin: mc.rewardInt || undefined,
        rewardMax: mc.rewardInt || undefined,
        nearestStation: mc.station || undefined,
        remoteType: mc.remoteFrequency || undefined,
        requiredSkills: mc.requiredSkills || undefined,
        niceToHaveSkills: mc.niceToHaveSkills || undefined,
        notes: mc.remarks || undefined,
        sourceEmailId: email.id,
      });
      toast.success("注力案件に登録しました");
      navigate("/crm/sales-board");
    } catch {
      toast.error("注力案件の登録に失敗しました");
    }
  }, [data?.email, createMatter, navigate]);

  // CRM: マッチング結果→パイプライン追加
  const handleAddToPipeline = useCallback(
    async (result: MatchingResultItem) => {
      try {
        await createPipeline({
          matterTitle: result.matchedSubject || undefined,
          stage: "preparation",
          opportunitySource: "matching",
          matchingResultId: result.id,
          opportunitySnapshot: JSON.stringify(result.matchedSnapshot),
        } as never);
        toast.success("営業進捗に追加しました");
        navigate("/crm/sales-board");
      } catch {
        toast.error("営業進捗の追加に失敗しました");
      }
    },
    [createPipeline, navigate],
  );

  const handleSendReply = useCallback(async (): Promise<boolean> => {
    const success = await reply.sendEmail();
    if (success) {
      toast.success("返信メールを送信しました");
      setRightPanelMode("results");
      reply.resetDraft();
    } else {
      toastError("メールの送信に失敗しました");
    }
    return success;
  }, [reply.sendEmail, reply.resetDraft]);

  const handleSendProposal = useCallback(async (): Promise<boolean> => {
    const success = await proposal.sendProposal();
    if (success) {
      toast.success("提案メールを送信しました");
      // ステータスを更新
      if (proposal.draft) {
        try {
          await updateResultStatus(proposal.draft.resultId, "proposed");
        } catch {
          // ステータス更新失敗は致命的ではない
        }
      }
      setRightPanelMode("results");
      proposal.resetDraft();
    } else {
      toastError("メールの送信に失敗しました");
    }
    return success;
  }, [
    proposal.sendProposal,
    proposal.draft,
    proposal.resetDraft,
    updateResultStatus,
  ]);

  if (error) {
    return (
      <div className="flex justify-center mt-4">
        <div className="text-left text-red-500 max-w-xl">
          <p>メールの取得でエラーが発生しました。</p>
        </div>
      </div>
    );
  }

  return (
    <EmailDetailPage
      isLoading={loading}
      email={data?.email || null}
      freeWords={freeWords}
      isMatchingEnabled={isMatchingEnabled}
      hasActiveMatchingCondition={hasActiveMatchingCondition}
      isMatchingLoading={matchingLoading}
      matchingError={matchingError}
      matchingJob={jobResult}
      onExecuteMatching={handleExecuteMatching}
      onDismissMatchingError={clearResult}
      rightPanelMode={rightPanelMode}
      proposalDraft={proposal.draft}
      isProposalGenerating={proposal.isGenerating}
      isProposalSending={proposal.isSending}
      proposalError={proposal.error}
      onPropose={handlePropose}
      onProposalFieldChange={proposal.setField}
      onSendProposal={handleSendProposal}
      onBackToResults={handleBackToResults}
      onCancelGeneration={proposal.cancelGeneration}
      // Reply
      replyDraft={reply.draft}
      isReplyGenerating={reply.isGenerating}
      isReplySending={reply.isSending}
      replyError={reply.error}
      onReply={handleReply}
      onReplyFieldChange={reply.setField}
      onAddRecipient={reply.addRecipient}
      onRemoveRecipient={reply.removeRecipient}
      onGenerateReplyBody={reply.generateBody}
      onSendReply={handleSendReply}
      onCancelReplyGeneration={reply.cancelGeneration}
      // Comparison
      comparisonTarget={comparisonTarget}
      onCompare={handleCompare}
      onCloseComparison={handleCloseComparison}
      onFeedback={upsertFeedback}
      // CRM連携（editor以上のみ作成・登録ボタン表示）
      isCrmEnabled={canCrmEdit}
      onRegisterAsTalent={handleRegisterAsTalent}
      isRegisteringTalent={isRegisteringTalent}
      onRegisterAsFocusMatter={handleRegisterAsFocusMatter}
      isRegisteringFocusMatter={isRegisteringFocusMatter}
      onAddToPipeline={handleAddToPipeline}
      isAddingToPipeline={isAddingToPipeline}
    />
  );
};
